import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Circle, ArrowRight } from 'lucide-react';
import { getOnboardingState } from '@/lib/onboarding-state';

interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
  link?: string;
}

export function DashboardChecklist() {
  const state = getOnboardingState();

  // Don't show if onboarding completed or never started with a form
  if (!state || state.currentStep === 'completed') {
    return null;
  }

  // Don't show if no form was created
  if (!state.formId) {
    return null;
  }

  // Don't show if recently skipped (within 24 hours)
  if (state.skippedAt) {
    const hoursSinceSkip = (Date.now() - new Date(state.skippedAt).getTime()) / (1000 * 60 * 60);
    if (hoursSinceSkip < 24) return null;
  }

  const items: ChecklistItem[] = [
    {
      id: 'form',
      label: 'Create your first form',
      completed: state.completedSteps.includes(1),
    },
    {
      id: 'telegram',
      label: 'Connect Telegram',
      completed: state.telegramConnected,
      link: state.formId ? `/app/forms/${state.formId}` : undefined,
    },
    {
      id: 'test',
      label: 'Send a test submission',
      completed: state.testSubmitted,
      link: state.formId ? `/app/forms/${state.formId}` : undefined,
    },
    {
      id: 'email',
      label: 'Set up email delivery (optional)',
      completed: state.emailConfigured,
      link: state.formId ? `/app/forms/${state.formId}` : undefined,
    },
  ];

  const completedCount = items.filter((i) => i.completed).length;
  const progress = (completedCount / items.length) * 100;

  // If all items completed, don't show
  if (completedCount === items.length) {
    return null;
  }

  return (
    <Card className="border-blue-200 bg-blue-50/50 mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Getting Started</CardTitle>
          <span className="text-sm text-gray-500">
            {completedCount}/{items.length} complete
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              {item.completed ? (
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-3 h-3 text-green-600" />
                </div>
              ) : (
                <Circle className="w-5 h-5 text-gray-300" />
              )}
              <span className={item.completed ? 'text-gray-500 line-through' : 'text-gray-700'}>
                {item.label}
              </span>
            </div>
            {!item.completed && item.link && (
              <Link to={item.link}>
                <Button variant="ghost" size="sm">
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
          </div>
        ))}

        {completedCount < items.length && (
          <div className="pt-2">
            <Link to="/app/setup">
              <Button variant="outline" size="sm" className="w-full">
                Continue Setup
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
